import { useEffect, useState } from 'react';
import { T, useGT } from 'gt-react';
import { TaskPrioritySchema, type TaskPriority } from '@erp/rpc';
import { MarkdownWYSIWYGEditor } from 'src/components/common/MarkdownWYSIWYGEditor';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';

const PRIORITIES: TaskPriority[] = ['urgent', 'high', 'medium', 'low'];

export interface TaskCreateSubmit {
  title: string;
  description: string | undefined;
  priority: TaskPriority;
  assigneeType?: 'agent' | 'user';
  assigneeUserId?: string;
}

export interface TaskCreateAgentOption {
  id: string;
  name: string;
}

interface TaskCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: TaskCreateSubmit) => Promise<void>;
  isSubmitting: boolean;
  agentOptions?: TaskCreateAgentOption[];
  agentDefinitionId?: string | null;
  onAgentChange?: (agentDefinitionId: string) => void;
  userOptions?: { id: string; name: string }[];
}

export function TaskCreateDialog({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
  agentOptions,
  agentDefinitionId,
  onAgentChange,
  userOptions,
}: TaskCreateDialogProps) {
  const gt = useGT();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [assigneeType, setAssigneeType] = useState<'agent' | 'user'>('agent');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const showAssigneeSection =
    agentOptions !== undefined || userOptions !== undefined;

  useEffect(() => {
    if (open) {
      setTitle('');
      setDescription('');
      setPriority('medium');
      setAssigneeType('agent');
      setSelectedUserId(null);
    }
  }, [open]);

  function handleAssigneeTypeChange(value: 'agent' | 'user') {
    setAssigneeType(value);
    if (value === 'user') {
      onAgentChange?.('');
    } else {
      setSelectedUserId(null);
    }
  }

  async function handleSubmit() {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return;
    const trimmedDescription = description.trim();
    await onSubmit({
      title: trimmedTitle,
      description:
        trimmedDescription.length > 0 ? trimmedDescription : undefined,
      priority,
      assigneeType: showAssigneeSection ? assigneeType : undefined,
      assigneeUserId:
        assigneeType === 'user' && selectedUserId ? selectedUserId : undefined,
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            <T>Create Task</T>
          </DialogTitle>
          <DialogDescription>
            <T>Assign a new task to this agent.</T>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {showAssigneeSection && (
            <div className="space-y-2">
              <Label>
                <T>Assign to</T>
              </Label>
              <RadioGroup
                value={assigneeType}
                onValueChange={(v) =>
                  handleAssigneeTypeChange(v as 'agent' | 'user')
                }
                className="flex items-center gap-4"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="agent" id="assignee-agent" />
                  <Label htmlFor="assignee-agent" className="font-normal">
                    <T>Agent</T>
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="user" id="assignee-user" />
                  <Label htmlFor="assignee-user" className="font-normal">
                    <T>User</T>
                  </Label>
                </div>
              </RadioGroup>
              {assigneeType === 'agent' && agentOptions && (
                <Select
                  value={agentDefinitionId ?? undefined}
                  onValueChange={(value) => onAgentChange?.(value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={gt('Select an agent')} />
                  </SelectTrigger>
                  <SelectContent>
                    {agentOptions.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {assigneeType === 'user' && userOptions && (
                <Select
                  value={selectedUserId ?? undefined}
                  onValueChange={setSelectedUserId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={gt('Select a user')} />
                  </SelectTrigger>
                  <SelectContent>
                    {userOptions.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="new-task-title">
              <T>Title</T>
            </Label>
            <Input
              id="new-task-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder={gt('What should the agent do?')}
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label>
              <T>Priority</T>
            </Label>
            <Select
              value={priority}
              onValueChange={(value) =>
                setPriority(TaskPrioritySchema.parse(value))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((value) => (
                  <SelectItem key={value} value={value}>
                    {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>
              <T>Description</T>
            </Label>
            <MarkdownWYSIWYGEditor
              key={open ? 'open' : 'closed'}
              initialValue={description}
              onChange={setDescription}
              placeholder={gt('Add task details')}
              height={180}
              showToolbar
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <T>Cancel</T>
          </Button>
          <Button
            onClick={() => {
              void handleSubmit();
            }}
            disabled={
              isSubmitting ||
              !title.trim() ||
              (showAssigneeSection &&
                ((assigneeType === 'agent' && !agentDefinitionId) ||
                  (assigneeType === 'user' && !selectedUserId)))
            }
          >
            {isSubmitting ? gt('Creating...') : gt('Create task')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
